import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { usePatient } from "@louise/patients/hooks/usePatient";
import { withMedicalHistoryRecordFixture } from "@src/packages/medical-history/test-fixtures/medicalHistoryRecordFixture";
import { useQueuedPatientUpdate } from "@louise/patients/sync/usePatientSync";
import type { Patient } from "@louise/medical-history/basic-info/calculateBasicInfoCompletion";
import { buildPregnancyNavigationStack } from "./buildPregnancyNavigationStack";
import { transformPregnancyStateToRequest } from "./transformPregnancyStateToRequest";
import type { PregnancyFlowRoute } from "./buildPregnancyNavigationStack";
import type { PregnancyOutcome, DeliveryType } from "./options";

const AUTOSAVE_DELAY_MS = 1500;

// ── Sub-resource detail types ──────────────────────────────

export interface PregnancyDetail {
  id?: string;
  startDate?: string | null;
  outcome?: PregnancyOutcome | null;
  deliveryDate?: string | null;
  deliveryType?: DeliveryType | null;
  withCurrentPartner?: boolean | null;
  notes?: string | null;
}

// ── State ──────────────────────────────────────────────────

export interface PregnancyState {
  hasBeenPregnant: boolean | null;
  pregnancies: PregnancyDetail[];
  pregnancyIdsToDelete: string[];
  /** Removed optimistic rows whose server IDs belong to an older FIFO write. */
  anonymousPregnanciesToDelete: number;
  navigationStack: PregnancyFlowRoute[];
  currentNavigationIndex: number;
}

const initialState: PregnancyState = {
  hasBeenPregnant: null,
  pregnancies: [],
  pregnancyIdsToDelete: [],
  anonymousPregnanciesToDelete: 0,
  navigationStack: ["PregnancyUsage"],
  currentNavigationIndex: 0,
};

// ── Read pregnancy entries from patient ────────────────────

function readPregnancyEntries(patient: Patient | null | undefined) {
  const obGyn = patient?.obGynHistory;
  return obGyn?.pregnancies ?? [];
}

// ── Build state from patient data ──────────────────────────

function buildStateFromPatient(
  patient: Patient | null | undefined,
): PregnancyState {
  const obGyn = patient?.obGynHistory;
  const hasBeenPregnant: boolean | null | undefined = obGyn?.hasBeenPregnant;
  const entries = readPregnancyEntries(patient);

  if (hasBeenPregnant === undefined || hasBeenPregnant === null) {
    return initialState;
  }

  const pregnancies: PregnancyDetail[] = entries.map((e) => ({
    id: e?.id ?? undefined,
    startDate: e?.startDate ?? null,
    outcome: e?.outcome ?? null,
    deliveryDate: e?.deliveryDate ?? null,
    deliveryType: e?.deliveryType ?? null,
    withCurrentPartner: e?.withCurrentPartner ?? null,
    notes: e?.notes ?? null,
  }));

  const base: PregnancyState = {
    hasBeenPregnant,
    pregnancies,
    pregnancyIdsToDelete: [],
    anonymousPregnanciesToDelete: 0,
    navigationStack: ["PregnancyUsage"],
    currentNavigationIndex: 0,
  };

  const stack = buildPregnancyNavigationStack(base);
  return { ...base, navigationStack: stack };
}

// ── Resume route ───────────────────────────────────────────

export function getResumeRoute(
  patient: Patient | null | undefined,
): PregnancyFlowRoute {
  const obGyn = patient?.obGynHistory;
  const hasBeenPregnant = obGyn?.hasBeenPregnant;
  const entries = readPregnancyEntries(patient);

  if (hasBeenPregnant === undefined || hasBeenPregnant === null) {
    return "PregnancyUsage";
  }
  if (hasBeenPregnant === false) return "PregnancyUsage";
  if (entries.length === 0) return "PregnancyDetails";

  const tempState = buildStateFromPatient(patient);
  const stack = tempState.navigationStack;

  const screenComplete: Record<PregnancyFlowRoute, () => boolean> = {
    PregnancyUsage: () => tempState.hasBeenPregnant !== null,
    PregnancyDetails: () => tempState.pregnancies.length > 0,
    PregnancyComplete: () => false,
  };

  for (const route of stack) {
    if (route === "PregnancyComplete") continue;
    if (!screenComplete[route]()) return route;
  }

  return stack[0] ?? "PregnancyUsage";
}

// ── Context ────────────────────────────────────────────────

interface PregnancyContextValue {
  state: PregnancyState;
  updateState: (updates: Partial<PregnancyState>) => void;
  resetState: () => void;
  save: () => Promise<void>;
}

const PregnancyContext = createContext<PregnancyContextValue | null>(null);

export function PregnancyProvider({ children }: { children: React.ReactNode }) {
  const { data: rawPatient } = usePatient();
  const patient = withMedicalHistoryRecordFixture(rawPatient);
  const { mutateAsync } = useQueuedPatientUpdate();

  const [state, setState] = useState<PregnancyState>(() =>
    buildStateFromPatient(patient),
  );
  const userEditedRef = useRef(false);
  const skipNextAutosaveRef = useRef(false);

  // Skip autosave on mount — only fire after user edits
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
  }, []);

  useEffect(() => {
    if (!patient) return;
    if (userEditedRef.current) {
      const serverPregnancies = readPregnancyEntries(patient);
      setState((prev) => {
        let changed = false;
        const pregnancies = prev.pregnancies.map((pregnancy, index) => {
          const serverId = serverPregnancies[index]?.id;
          if (pregnancy.id || !serverId) return pregnancy;
          changed = true;
          return { ...pregnancy, id: serverId };
        });
        return changed ? { ...prev, pregnancies } : prev;
      });
      return;
    }

    setState((prev) => {
      const isEmpty =
        prev.hasBeenPregnant === null &&
        prev.pregnancies.length === 0 &&
        prev.pregnancyIdsToDelete.length === 0 &&
        prev.anonymousPregnanciesToDelete === 0;
      if (!isEmpty) return prev;
      skipNextAutosaveRef.current = true;
      return buildStateFromPatient(patient);
    });
  }, [patient]);

  const updateState = useCallback((updates: Partial<PregnancyState>) => {
    userEditedRef.current = true;
    setState((prev) => {
      const newState = { ...prev, ...updates };
      const stack = buildPregnancyNavigationStack(newState);
      return {
        ...newState,
        navigationStack: stack,
        currentNavigationIndex: Math.min(
          newState.currentNavigationIndex ?? 0,
          Math.max(stack.length - 1, 0),
        ),
      };
    });
  }, []);

  const save = useCallback(async () => {
    const payload = transformPregnancyStateToRequest(state);
    if (Object.keys(payload).length === 0) return;
    const submittedDeleteIds = new Set(state.pregnancyIdsToDelete);
    const submittedAnonymousDeletes = state.anonymousPregnanciesToDelete;

    const obGynHistoryId = patient?.obGynHistory?.id;
    if (payload.obGynHistory && obGynHistoryId) {
      payload.obGynHistory.id = obGynHistoryId;
    }

    try {
      await mutateAsync(payload);
      setState((prev) => ({
        ...prev,
        pregnancyIdsToDelete: prev.pregnancyIdsToDelete.filter(
          (id) => !submittedDeleteIds.has(id),
        ),
        anonymousPregnanciesToDelete: Math.max(
          0,
          prev.anonymousPregnanciesToDelete - submittedAnonymousDeletes,
        ),
      }));
    } catch (error) {
      throw error;
    }
  }, [state, mutateAsync, patient]);

  // ── Debounced autosave on state changes ──────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!mountedRef.current) return;
    if (skipNextAutosaveRef.current) {
      skipNextAutosaveRef.current = false;
      return;
    }
    // State also arrives from the record itself — through the lazy initialiser
    // when the query has already resolved, which leaves the skip above unarmed
    // — and saving that back rewrites answers the patient never touched on
    // this visit.
    if (!userEditedRef.current) return;
    if (state.hasBeenPregnant === null) return;

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    autosaveTimerRef.current = setTimeout(() => {
      save();
    }, AUTOSAVE_DELAY_MS);

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, [state, save]);

  const resetState = useCallback(() => setState(initialState), []);

  return (
    <PregnancyContext.Provider value={{ state, updateState, resetState, save }}>
      {children}
    </PregnancyContext.Provider>
  );
}

export function usePregnancy() {
  const ctx = useContext(PregnancyContext);
  if (!ctx) {
    throw new Error("usePregnancy must be used within PregnancyProvider");
  }
  return ctx;
}
